import React, { useState } from 'react'
import { Modal, Pressable, View } from 'react-native'
import { StyleSheet, useUnistyles } from 'react-native-unistyles'

import UniButton from '@/components/unistyles/UniButton'
import UniText from '@/components/unistyles/UniText'

// Web Alert implementation using React Native components
interface AlertButton {
  text?: string
  onPress?: () => void
  style?: 'default' | 'cancel' | 'destructive'
  isPreferred?: boolean
}

type AlertType = 'default' | 'plain-text' | 'secure-text' | 'login-password'

interface AlertOptions {
  cancelable?: boolean
  userInterfaceStyle?: 'unspecified' | 'light' | 'dark'
}

interface AlertState {
  visible: boolean
  title: string
  message?: string
  buttons: AlertButton[]
}

// Global state for the alert
const globalAlertState: AlertState = {
  visible: false,
  title: '',
  message: '',
  buttons: [],
}

let setGlobalAlertState: React.Dispatch<
  React.SetStateAction<AlertState>
> | null = null

// Alert Modal Component
const AlertModal: React.FC = () => {
  const { theme } = useUnistyles()
  const [alertState, setAlertState] = useState<AlertState>(globalAlertState)

  // Set the global state setter
  React.useEffect(() => {
    setGlobalAlertState = setAlertState
  }, [])

  const handleButtonPress = (button: AlertButton) => {
    setAlertState((prev) => ({ ...prev, visible: false }))
    if (button.onPress) {
      // Add a small delay to ensure modal closes smoothly
      setTimeout(() => {
        button.onPress!()
      }, 100)
    }
  }

  const handleBackdropPress = () => {
    const cancelButton = alertState.buttons.find((b) => b.style === 'cancel')
    if (cancelButton) {
      handleButtonPress(cancelButton)
    } else {
      // If no cancel button, just close
      setAlertState((prev) => ({ ...prev, visible: false }))
    }
  }

  // Sort buttons: cancel first, then others
  const sortedButtons = [...alertState.buttons].sort((a, b) => {
    if (a.style === 'cancel' && b.style !== 'cancel') return -1
    if (a.style !== 'cancel' && b.style === 'cancel') return 1
    return 0
  })

  return (
    <Modal
      animationType="fade"
      onRequestClose={handleBackdropPress}
      transparent
      visible={alertState.visible}
    >
      <Pressable
        accessibilityRole="button"
        onPress={handleBackdropPress}
        style={styles.overlay}
      >
        <Pressable
          accessibilityRole="button"
          onPress={() => {}}
          style={styles.dialog}
        >
          <UniText style={styles.title}>{alertState.title}</UniText>
          {alertState.message && (
            <UniText style={styles.message}>{alertState.message}</UniText>
          )}
          <View style={styles.buttonContainer}>
            {sortedButtons.map((button, index) => (
              <UniButton
                buttonColor={
                  button.style === 'destructive'
                    ? theme.colors.error
                    : undefined
                }
                key={index}
                mode={button.style === 'cancel' ? 'outlined' : 'contained'}
                onPress={() => handleButtonPress(button)}
                style={styles.button}
                textColor={
                  button.style === 'destructive'
                    ? theme.colors.onError
                    : undefined
                }
              >
                {button.text || 'OK'}
              </UniButton>
            ))}
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  )
}

class AlertWeb {
  static alert(
    title: string,
    message?: string,
    buttons?: AlertButton[],
    _options?: AlertOptions,
    _type?: AlertType
  ): void {
    const alertButtons = buttons || [{ text: 'OK' }]

    if (setGlobalAlertState) {
      setGlobalAlertState({
        visible: true,
        title,
        message,
        buttons: alertButtons,
      })
    }
  }

  static prompt(
    title: string,
    message?: string,
    callbackOrButtons?: ((text: string | null) => void) | AlertButton[],
    _type?: AlertType,
    _defaultValue?: string,
    _keyboardType?: string
  ): void {
    // For now, fallback to browser prompt for prompt functionality
    // Could be enhanced with a custom TextInput modal later
    const result = window.prompt(
      `${title}${message ? `\n\n${message}` : ''}`,
      _defaultValue
    )

    if (typeof callbackOrButtons === 'function') {
      callbackOrButtons(result)
    }
  }
}

// Export the modal component so it can be rendered at app root
export { AlertModal }

const styles = StyleSheet.create((theme) => ({
  button: {
    minWidth: 80,
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: theme.tokens.spacing[2],
    justifyContent: 'flex-end',
  },
  dialog: {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.tokens.spacing[3],
    elevation: 25,
    maxWidth: 400,
    minWidth: 300,
    padding: theme.tokens.spacing[4],
    shadowColor: theme.colors.shadow,
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.3,
    shadowRadius: 25,
  },
  message: {
    color: theme.colors.onSurface,
    fontSize: 13,
    lineHeight: 18,
    marginBottom: theme.tokens.spacing[6],
    textAlign: 'center',
  },
  overlay: {
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    flex: 1,
    justifyContent: 'center',
  },
  title: {
    color: theme.colors.onSurface,
    fontSize: 17,
    fontWeight: '600',
    marginBottom: theme.tokens.spacing[3],
    textAlign: 'left',
  },
}))

export default AlertWeb
